import { Search } from '@mui/icons-material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { accents } from 'sutra/tokens/colors'

import {
  type BreakdownSelection,
  isCustomBreakdown,
} from 'features/charts-v2/components/breakdown-selection-utils'
import { RadioListOption } from 'features/charts-v2/components/RadioListOption'
import { useAbsolutePopoverPlacement } from 'features/charts-v2/hooks/use-popover-placement'
import {
  type BreakdownOption,
  BREAKDOWNS,
  capitalizeModel,
  type CustomBreakdown,
} from 'features/charts-v2/types'
import { useEscapeDismiss } from 'hooks/use-escape-dismiss'

interface BreakdownSelectionPopoverProps {
  onBreakdownSelect: (breakdown: BreakdownSelection) => void
  onClose: () => void
  selectedBreakdown?: BreakdownSelection
  // Constraint props
  invalidBreakdowns?: BreakdownOption[]
  getInvalidReason?: (breakdown: BreakdownOption) => string | null
  // Custom breakdowns from the project
  customBreakdowns?: CustomBreakdown[]
  // All currently selected breakdowns (to prevent duplicates)
  allSelectedBreakdowns?: BreakdownSelection[]
  // Ref to the trigger element to prevent closing when clicking it
  triggerRef?: React.RefObject<HTMLElement>
  // Whether to show the model hierarchy prefix (e.g. "Post > Theme")
  showBreakdownHierarchy: boolean
}

const STANDARD_BREAKDOWN_OPTIONS: BreakdownOption[] = [
  BREAKDOWNS.BRAND.id,
  BREAKDOWNS.PRODUCT.id,
  BREAKDOWNS.PROMOTIONAL.id,
  BREAKDOWNS.THEME.id,
  BREAKDOWNS.SOURCE_TYPE.id,
  BREAKDOWNS.COUNTRY.id,
  BREAKDOWNS.DATASET.id,
  BREAKDOWNS.SENTIMENT.id,
  BREAKDOWNS.RATING.id,
  // TIME removed - now handled separately in ChartCommandBar for LINE charts
]

// Helper to get label from breakdown ID
const getBreakdownLabel = (breakdownId: BreakdownOption): string => {
  const breakdown = Object.values(BREAKDOWNS).find((b) => b.id === breakdownId)
  return breakdown?.label || breakdownId
}

export const BreakdownSelectionPopover: React.FC<
  BreakdownSelectionPopoverProps
> = ({
  onBreakdownSelect,
  onClose,
  selectedBreakdown,
  invalidBreakdowns = [],
  getInvalidReason,
  customBreakdowns = [],
  allSelectedBreakdowns = [],
  triggerRef,
  showBreakdownHierarchy,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const highlightedItemRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const { isMeasured, style } = useAbsolutePopoverPlacement({ popoverRef })

  // Auto-focus the search input when the popover opens
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [])

  useEscapeDismiss({ onClose })

  // Handle click outside to close popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node
      const isInsidePopover = popoverRef.current?.contains(target)
      const isInsideTrigger = triggerRef?.current?.contains(target)

      if (!isInsidePopover && !isInsideTrigger) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose, triggerRef])

  // Filter standard breakdown options
  const filteredStandardOptions = STANDARD_BREAKDOWN_OPTIONS.filter((option) =>
    getBreakdownLabel(option).toLowerCase().includes(searchTerm.toLowerCase()),
  )

  // Filter custom breakdown options
  const filteredCustomOptions = useMemo(() => {
    if (!searchTerm) return customBreakdowns
    return customBreakdowns.filter((cb) =>
      cb.alias.toLowerCase().includes(searchTerm.toLowerCase()),
    )
  }, [customBreakdowns, searchTerm])

  // Helper function to check if a standard breakdown is invalid
  // (defined early so it can be used in navigableItems useMemo)
  const isBreakdownInvalid = useCallback(
    (breakdown: BreakdownOption): boolean => {
      return invalidBreakdowns.includes(breakdown)
    },
    [invalidBreakdowns],
  )

  // Helper to get unique ID for any breakdown
  const getBreakdownId = useCallback(
    (breakdown: BreakdownSelection | undefined): string => {
      if (!breakdown) return 'none'
      if (breakdown.kind === 'standard') return breakdown.id
      if (breakdown.kind === 'custom') {
        return `custom:${breakdown.model}:${breakdown.field}`
      }
      return 'none'
    },
    [],
  )

  // Helper to convert legacy breakdown option to v2
  const toBreakdownV2 = useCallback(
    (breakdown: BreakdownOption | CustomBreakdown): BreakdownSelection => {
      if (typeof breakdown === 'string') {
        const isGroupable = ['brand', 'product', 'theme'].includes(breakdown)
        return {
          kind: 'standard',
          id: breakdown,
          ...(isGroupable && { grouped: true }),
        } as BreakdownSelection
      } else {
        return {
          kind: 'custom',
          field: breakdown.field,
          alias: breakdown.alias,
          model: breakdown.model,
        }
      }
    },
    [],
  )

  // Helper to check if a breakdown is already selected elsewhere
  const isBreakdownAlreadySelected = useCallback(
    (breakdown: BreakdownSelection): boolean => {
      if (!breakdown) return false
      const breakdownId = getBreakdownId(breakdown)
      const selectedId = getBreakdownId(selectedBreakdown)

      // Check if this breakdown exists in allSelectedBreakdowns,
      // but exclude the current selectedBreakdown (the one being edited)
      return allSelectedBreakdowns.some((b) => {
        const bId = getBreakdownId(b)
        return bId === breakdownId && bId !== selectedId
      })
    },
    [allSelectedBreakdowns, selectedBreakdown, getBreakdownId],
  )

  // Wrappers for legacy types
  const isStandardBreakdownAlreadySelected = useCallback(
    (breakdown: BreakdownOption): boolean => {
      return isBreakdownAlreadySelected(toBreakdownV2(breakdown))
    },
    [isBreakdownAlreadySelected, toBreakdownV2],
  )

  const isCustomBreakdownAlreadySelected = useCallback(
    (breakdown: CustomBreakdown): boolean => {
      return isBreakdownAlreadySelected(toBreakdownV2(breakdown))
    },
    [isBreakdownAlreadySelected, toBreakdownV2],
  )

  // Create a flat list of navigable items for keyboard navigation
  type NavigableItem =
    | { type: 'standard'; option: BreakdownOption }
    | { type: 'custom'; breakdown: CustomBreakdown }

  const navigableItems = useMemo<NavigableItem[]>(() => {
    const items: NavigableItem[] = []

    // Add standard options (skip invalid ones and already-selected ones for navigation)
    filteredStandardOptions.forEach((option) => {
      if (
        !isBreakdownInvalid(option) &&
        !isStandardBreakdownAlreadySelected(option)
      ) {
        items.push({ type: 'standard', option })
      }
    })

    // Add custom options (skip already-selected ones)
    filteredCustomOptions.forEach((breakdown) => {
      if (!isCustomBreakdownAlreadySelected(breakdown)) {
        items.push({ type: 'custom', breakdown })
      }
    })

    return items
  }, [
    filteredStandardOptions,
    filteredCustomOptions,
    isBreakdownInvalid,
    isStandardBreakdownAlreadySelected,
    isCustomBreakdownAlreadySelected,
  ])

  // Reset highlighted index when search term or navigable items change
  useEffect(() => {
    setHighlightedIndex(navigableItems.length > 0 ? 0 : -1)
  }, [searchTerm, navigableItems.length])

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedItemRef.current && highlightedIndex >= 0) {
      highlightedItemRef.current.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'nearest',
      })
    }
  }, [highlightedIndex])

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (navigableItems.length === 0) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setHighlightedIndex((prev) =>
          prev < navigableItems.length - 1 ? prev + 1 : prev,
        )
        break
      case 'ArrowUp':
        event.preventDefault()
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev))
        break
      case 'Enter':
        event.preventDefault()
        if (highlightedIndex >= 0 && highlightedIndex < navigableItems.length) {
          const item = navigableItems[highlightedIndex]
          if (item != null && item.type === 'standard') {
            handleSelectStandard(item.option)
          } else if (item != null) {
            handleSelectCustom(item.breakdown)
          }
        }
        break
      // Escape is handled by useEscapeDismiss above — it needs to work
      // regardless of whether this popover holds DOM focus, so it isn't
      // duplicated here.
    }
  }

  // Helper to get the index of a standard option in the navigable items list
  const getNavigableIndexForStandard = (option: BreakdownOption): number => {
    return navigableItems.findIndex(
      (item) => item.type === 'standard' && item.option === option,
    )
  }

  // Helper to get the index of a custom breakdown in the navigable items list
  const getNavigableIndexForCustom = (breakdown: CustomBreakdown): number => {
    return navigableItems.findIndex(
      (item) =>
        item.type === 'custom' &&
        item.breakdown.field === breakdown.field &&
        item.breakdown.model === breakdown.model,
    )
  }

  const handleSelectStandard = (breakdown: BreakdownOption): void => {
    // Don't allow selection of invalid breakdowns
    if (invalidBreakdowns.includes(breakdown)) {
      return
    }
    // Don't allow selection of already-selected breakdowns
    if (isStandardBreakdownAlreadySelected(breakdown)) {
      return
    }

    // Convert to v2 Breakdown object
    const breakdownV2 = toBreakdownV2(breakdown)
    onBreakdownSelect(breakdownV2)
  }

  const handleSelectCustom = (customBreakdown: CustomBreakdown): void => {
    // Don't allow selection of already-selected breakdowns
    if (isCustomBreakdownAlreadySelected(customBreakdown)) {
      return
    }

    // Convert to v2 CustomBreakdown object
    const breakdownV2 = toBreakdownV2(customBreakdown)
    onBreakdownSelect(breakdownV2)
  }

  // Check if a custom breakdown is currently selected
  const isCustomSelected = (cb: CustomBreakdown): boolean => {
    if (!selectedBreakdown) return false
    return (
      isCustomBreakdown(selectedBreakdown) &&
      selectedBreakdown.field === cb.field &&
      selectedBreakdown.model === cb.model
    )
  }

  // Check if a standard breakdown is currently selected
  const isStandardSelected = (option: BreakdownOption): boolean => {
    if (!selectedBreakdown) return false
    return (
      selectedBreakdown.kind === 'standard' && selectedBreakdown.id === option
    )
  }

  const hasCustomBreakdowns = filteredCustomOptions.length > 0
  const hasNoResults =
    filteredStandardOptions.length === 0 && filteredCustomOptions.length === 0

  return (
    <div
      ref={popoverRef}
      data-yogi-overlay="true"
      style={{
        position: 'absolute',
        left: '0',
        right: '0',
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
        maxHeight: 350,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        visibility: isMeasured ? 'visible' : 'hidden',
        ...style,
      }}
    >
      {/* Search */}
      <div style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
        <div style={{ position: 'relative' }}>
          <Search
            sx={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              fontSize: '18px',
              pointerEvents: 'none',
            }}
          />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{
              width: '100%',
              padding: '8px 12px 8px 34px',
              border: `2px solid ${accents.reflectionBlue[500]}`,
              borderRadius: '6px',
              fontSize: '13px',
              outline: 'none',
            }}
          />
        </div>
      </div>

      {/* Options List */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '12px',
          minHeight: 0,
        }}
      >
        {/* Standard Breakdowns Section */}
        {filteredStandardOptions.length > 0 && (
          <>
            {hasCustomBreakdowns && (
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  padding: '0 8px 4px',
                }}
              >
                Standard
              </div>
            )}
            {filteredStandardOptions.map((option) => {
              const isSelected = isStandardSelected(option)
              const isInvalid = isBreakdownInvalid(option)
              const isAlreadySelected =
                isStandardBreakdownAlreadySelected(option)
              const isDisabledOption = isInvalid || isAlreadySelected
              const invalidReason = isInvalid
                ? getInvalidReason?.(option)
                : isAlreadySelected
                  ? 'Already selected'
                  : null
              const navIndex = getNavigableIndexForStandard(option)
              const isHighlighted = navIndex === highlightedIndex

              return (
                <RadioListOption
                  key={option}
                  ref={isHighlighted ? highlightedItemRef : null}
                  label={getBreakdownLabel(option)}
                  prefix={
                    showBreakdownHierarchy
                      ? Object.values(BREAKDOWNS).find((b) => b.id === option)
                          ?.model || ''
                      : undefined
                  }
                  isSelected={isSelected}
                  isHighlighted={isHighlighted}
                  isDisabled={isDisabledOption}
                  disabledReason={invalidReason || undefined}
                  onClick={() => handleSelectStandard(option)}
                />
              )
            })}
          </>
        )}

        {/* Custom Breakdowns Section */}
        {filteredCustomOptions.length > 0 && (
          <>
            <div
              style={{
                fontSize: '12px',
                fontWeight: '600',
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                padding: '12px 8px 4px',
                marginTop: filteredStandardOptions.length > 0 ? '8px' : '0',
                borderTop:
                  filteredStandardOptions.length > 0
                    ? '1px solid #f3f4f6'
                    : 'none',
              }}
            >
              Custom Fields
            </div>
            {filteredCustomOptions.map((customBreakdown) => {
              const isSelected = isCustomSelected(customBreakdown)
              const isAlreadySelected =
                isCustomBreakdownAlreadySelected(customBreakdown)
              const navIndex = getNavigableIndexForCustom(customBreakdown)
              const isHighlighted = navIndex === highlightedIndex

              return (
                <RadioListOption
                  key={`custom-${customBreakdown.model}-${customBreakdown.field}`}
                  ref={isHighlighted ? highlightedItemRef : null}
                  label={customBreakdown.alias}
                  prefix={
                    showBreakdownHierarchy
                      ? capitalizeModel(customBreakdown.model) || undefined
                      : undefined
                  }
                  isSelected={isSelected}
                  isHighlighted={isHighlighted}
                  isDisabled={isAlreadySelected}
                  disabledReason={
                    isAlreadySelected ? 'Already selected' : undefined
                  }
                  onClick={() => handleSelectCustom(customBreakdown)}
                />
              )
            })}
          </>
        )}

        {hasNoResults && (
          <div
            style={{
              padding: '20px',
              textAlign: 'center',
              color: '#6b7280',
              fontSize: '13px',
            }}
          >
            No breakdown options found.
          </div>
        )}
      </div>
    </div>
  )
}
